import { toBcp47 } from "@/lib/locale-bcp47";

/**
 * Format an ISO date string as a locale-aware short date
 * (e.g. `2 dec 2025` in nl, `2 Dez. 2025` in de, `2 Dec 2025` in en).
 *
 * Falls back to an empty string for missing input and to the raw ISO
 * for un-parseable input so a malformed review still renders instead
 * of blanking the row.
 */
export function formatReviewDate(iso: string, locale: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const bcp47 = toBcp47(locale);
  return d.toLocaleDateString(bcp47, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Coarse language-detection heuristic for reviewer content. Used when
 * the review payload has no `language` field: we pattern-match against
 * language-specific characters plus a very small list of common
 * function words. Cheap, no library dependency, deliberately
 * conservative: an "unknown" bucket beats a wrong bucket because the
 * locale filter always allows English + Dutch as fallbacks.
 *
 * Returns a lowercase 2-letter code (`nl` | `de` | `fr` | `es` | `it`
 * | `en`) or `null` when confidence is low.
 */
export function detectReviewLanguage(text: string): string | null {
  if (!text) return null;
  const t = text.toLowerCase();

  // Character-class hits, cheap and high-signal.
  if (/[ßẞ]/.test(t)) return "de";
  if (/[ñ¿¡]/.test(t)) return "es";

  // Function-word buckets. Each list is short, high-frequency, and
  // mostly exclusive to that language.
  const buckets: Array<[string, RegExp]> = [
    ["nl", /\b(en|het|een|niet|maar|voor|met|heel|goed|erg|zeer|prima|snel|dank|super|fijn|mooi)\b/],
    ["de", /\b(und|der|die|das|nicht|aber|fuer|für|mit|sehr|gut|schnell|danke|schoen|schön|toll|super)\b/],
    ["fr", /\b(et|le|la|les|une?|pas|mais|pour|avec|tres|très|bien|merci|super|rapide|parfait)\b/],
    ["es", /\b(y|el|la|los|las|una?|no|pero|para|con|muy|bien|gracias|rapido|perfecto)\b/],
    ["it", /\b(e|il|la|i|le|un[oa]?|non|ma|per|con|molto|bene|grazie|veloce|perfetto)\b/],
    ["en", /\b(and|the|a|an|not|but|for|with|very|good|great|fast|thanks|nice|love|amazing)\b/],
  ];

  const scores = buckets.map<[string, number]>(([lang, re]) => {
    const matches = t.match(new RegExp(re.source, "g"));
    return [lang, matches ? matches.length : 0];
  });

  scores.sort((a, b) => b[1] - a[1]);
  const [topLang, topScore] = scores[0];
  const runnerUpScore = scores[1]?.[1] ?? 0;

  // Require at least one hit and a clear margin over the runner-up.
  // A tied score (e.g. one hit each in nl + de) is ambiguous, return null.
  if (topScore < 1 || topScore <= runnerUpScore) return null;
  return topLang;
}

/**
 * Filter reviews to those matching the current locale. Uses the
 * review's `language` field when present; otherwise falls back to the
 * `detectReviewLanguage` heuristic against `content`/`title`.
 *
 * Fallback rule: if fewer than `minMatches` reviews match, the caller
 * ends up with the full unfiltered set (better a mixed carousel than
 * an empty one). English is treated as a soft-fallback tier so
 * en-review-heavy tenants still populate non-EN storefronts.
 */
export interface FilterableReview {
  language?: string | null;
  content?: string | null;
  title?: string | null;
}
export function filterReviewsByLocale<T extends FilterableReview>(
  reviews: T[],
  locale: string,
  minMatches = 2,
): T[] {
  if (!reviews.length) return reviews;

  const languageOf = (r: T): string | null => {
    if (r.language && r.language.length >= 2) return r.language.slice(0, 2).toLowerCase();
    return detectReviewLanguage(r.content || r.title || "");
  };

  const matching = reviews.filter((r) => languageOf(r) === locale);
  if (matching.length >= minMatches) return matching;

  // Priority-fallback: locale, then English, then everything.
  if (locale !== "en") {
    const englishMatches = reviews.filter((r) => languageOf(r) === "en");
    if (matching.length + englishMatches.length >= minMatches) {
      const seen = new Set(matching);
      return [
        ...matching,
        ...englishMatches.filter((r) => !seen.has(r)),
      ];
    }
  }

  // Give up on filtering: mixed carousel beats an empty one.
  return reviews;
}
